import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/authentication/authService';
import { AUTH_CONFIG, updateRefreshInterval } from '../../constants/authConfig';
import logger from '../../utils/logger';
import { LOGIN_PATH } from '../../constants/uploadConfig';
import { apiFetch } from '../../utils/apiClient';
import {
    clearRefreshDueTimeMs,
    getRefreshDueTimeMs,
    setRefreshDueTimeMs,
} from '../../services/authentication/tokenStore';
import { addWarningNotification } from './notificationSlice';
import { assignLocation, replaceLocation } from '../../utils/navigation';
import {
    AUTH_ERROR_CODES,
    AUTH_NOTIFICATION_MESSAGES,
    AUTH_NOTIFICATION_DELAYS,
} from '../../constants/authNotifications';

const isGroupAccessDenied = (error) =>
    error?.status === 403
    && error?.code
        === AUTH_ERROR_CODES.USER_NOT_ENTITLED;

const updateTokenRefreshTiming = (refreshIntervalSeconds) => {
    const intervalSeconds = Number(refreshIntervalSeconds);

    if (!Number.isFinite(intervalSeconds) || intervalSeconds <= 0) {
        return;
    }

    updateRefreshInterval(intervalSeconds);

    const refreshDueTime = Date.now() + intervalSeconds * 1000;
    setRefreshDueTimeMs(refreshDueTime);
};


// Async thunks
export const signIn = createAsyncThunk(
    'auth/signIn',
    async (_, { rejectWithValue }) => {
        try {
            const { redirectUrl } = await authService.getSignInRedirectUrl();
            assignLocation(redirectUrl);
            return true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const completeAuthentication = createAsyncThunk(
    'auth/completeAuthentication',
    async (code, { rejectWithValue }) => {
        try {
            const response = await authService.exchangeAuthorizationCode({ code });
            updateTokenRefreshTiming(response?.refresh_interval);
            return response; // { expires_in, refresh_interval, message, ... }
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const refreshToken = createAsyncThunk(
    'auth/refreshToken',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await authService.refreshSession();
            updateTokenRefreshTiming(response?.refresh_interval);
            return response; // { expires_in, refresh_interval, message, ... }
        } catch (error) {
            if (isGroupAccessDenied(error)) {
                return rejectWithValue(error.message);
            }

            if (error.status === 401 || error.status === 403) {
                logger.error('Token refresh failed after 3 retries. Redirecting to login.');
                dispatch(clearAuth());
                dispatch(stopPeriodicRefresh());
                replaceLocation(LOGIN_PATH);
            }

            return rejectWithValue(error.message);
        }
    }
);

export const signOut = createAsyncThunk(
    'auth/signOut',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            dispatch(clearAuth());
            dispatch(stopPeriodicRefresh());
            const { redirectUrl } = await authService.logout();
            replaceLocation(redirectUrl || LOGIN_PATH);
            return true;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Periodic refresh
let refreshTimeoutId = null;

export const startPeriodicRefresh = (delayOverrideMs = null) => (dispatch) => {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
    }

    const hasOverride = Number.isFinite(delayOverrideMs) && delayOverrideMs >= 0;
    const delayMs = hasOverride ? delayOverrideMs : AUTH_CONFIG.TOKEN_REFRESH_INTERVAL;

    setRefreshDueTimeMs(Date.now() + delayMs);

    refreshTimeoutId = setTimeout(async () => {
        refreshTimeoutId = null;
        try {
            logger.debug('🔄 Starting periodic token refresh...');
            await dispatch(refreshToken()).unwrap();
            dispatch(startPeriodicRefresh());
        } catch (error) {
            logger.error('❌ Periodic token refresh failed:', error);
        }
    }, delayMs);
};

export const stopPeriodicRefresh = () => () => {
    clearRefreshDueTimeMs();

    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
        logger.debug('🛑 Stopped periodic token refresh');
    }
};

export const handleNoDataProvider =
    (logoutErrorLogPrefix = 'Logout failed while restoring auth state:') =>
    (dispatch) => {
        dispatch(
            addWarningNotification({
                message: AUTH_NOTIFICATION_MESSAGES.NO_DATA_PROVIDER_ASSIGNED,
                isInline: true,
            })
        );

        setTimeout(async () => {
            dispatch(clearAuth());
            dispatch(stopPeriodicRefresh());

            try {
                const { redirectUrl } = await authService.logout();
                replaceLocation(redirectUrl || LOGIN_PATH);
            } catch (logoutError) {
                logger.error(logoutErrorLogPrefix, logoutError);
                replaceLocation(LOGIN_PATH);
            }
        }, AUTH_NOTIFICATION_DELAYS.LOGOUT_AFTER_WARNING_MS);
    };

// Restore auth state by checking if cookies are valid
export const restoreAuthState = () => async (dispatch) => {
    try {
        logger.debug('🔄 Checking if session is still valid...');

        await apiFetch('/api/v1/users/me', {
            method: 'GET',
            credentials: 'include',
            retry: false,
        });

        const refreshDueTime = getRefreshDueTimeMs();
        const delayMs = refreshDueTime
            ? Math.max(0, refreshDueTime - Date.now())
            : AUTH_CONFIG.TOKEN_REFRESH_INTERVAL;

        dispatch(setAuthenticated());
        dispatch(startPeriodicRefresh(delayMs));
        logger.debug('Session restored successfully');
        return true;
    } catch (error) {
        if (isGroupAccessDenied(error)) {
            return true;
        }

        if (error?.status === 401 || error?.status === 403) {
            dispatch(clearAuth());
            dispatch(stopPeriodicRefresh());
            if (window.location.pathname !== LOGIN_PATH) {
                replaceLocation(LOGIN_PATH);
            }
            return false;
        }

        if (error?.status === 417) {
            dispatch(handleNoDataProvider());

            return false;
        }

        logger.debug('No valid session (cookies expired or missing)');
        return false;
    }
};

// Initial state
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

// Auth slice
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setAuthenticated: (state) => {
            state.isAuthenticated = true;
        },
        clearAuth: (state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signIn.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signIn.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(signIn.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(completeAuthentication.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(completeAuthentication.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
            })
            .addCase(completeAuthentication.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(refreshToken.pending, (state) => {
                state.error = null;
            })
            .addCase(refreshToken.fulfilled, (state) => {
                state.isAuthenticated = true;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(signOut.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(signOut.fulfilled, (state) => {
                state.user = null;
                state.isAuthenticated = false;
                state.isLoading = false;
            })
            .addCase(signOut.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setAuthenticated, clearAuth } = authSlice.actions;
export default authSlice.reducer;